import { api } from './client';
import {
  BlogPageResponse,
  BulkStatusUpdateRequest,
  BulkStatusUpdateResponse,
  BulkTagsUpdateRequest,
  BulkTagsUpdateResponse,
  CreatePostRequest,
  FindMediaQuery,
  ListPostsQuery,
  ListTagsQuery,
  MediaDto,
  MediaRegisterRequest,
  OperationScheduledResponse,
  PostDto,
  PostImportRequest,
  PostMediaHeroRequest,
  PostMediaItemsRequest,
  PostMediaReorderRequest,
  PostMediaRole,
  PostMediaUpdateResponse,
  PostPreviewRequest,
  PostPreviewResponse,
  PostSearchHitDto,
  PostSummaryDto,
  PostTagsUpdateRequest,
  PublishPostRequest,
  ReadingTimeRecalculationResponse,
  SchedulePostRequest,
  SearchPostsQuery,
  SlugAvailabilityResponse,
  SlugGenerationRequest,
  SlugGenerationResponse,
  TagAssignmentResponse,
  TagCreateRequest,
  TagDto,
  TagRemovalResponse,
  TagUpdateRequest,
  UpdatePostRequest,
  UnusedMediaQuery
} from './blogTypes';

const buildQueryString = (configure: (params: URLSearchParams) => void): string => {
  const params = new URLSearchParams();
  configure(params);
  const query = params.toString();
  return query ? `?${query}` : '';
};

const appendParam = (
  params: URLSearchParams,
  key: string,
  value: string | number | boolean | null | undefined
): void => {
  if (value === undefined || value === null) {
    return;
  }
  params.set(key, typeof value === 'boolean' ? String(value) : String(value));
};

const appendTrimmedParam = (params: URLSearchParams, key: string, value: string | undefined): void => {
  if (value === undefined) {
    return;
  }
  const trimmed = value.trim();
  if (trimmed.length > 0) {
    params.set(key, trimmed);
  }
};

export const createPost = async (payload: CreatePostRequest): Promise<PostDto> => {
  const { data } = await api.post<PostDto>('/api/admin/v1/posts', payload);
  return data;
};

export const getPost = async (id: number): Promise<PostDto> => {
  const { data } = await api.get<PostDto>(`/api/admin/v1/posts/${id}`);
  return data;
};

export const updatePost = async (id: number, payload: UpdatePostRequest): Promise<PostDto> => {
  const { data } = await api.patch<PostDto>(`/api/admin/v1/posts/${id}`, payload);
  return data;
};

export const deletePost = async (id: number): Promise<void> => {
  await api.delete(`/api/admin/v1/posts/${id}`);
};

export const listPosts = async (query: ListPostsQuery = {}): Promise<BlogPageResponse<PostSummaryDto>> => {
  const queryString = buildQueryString((params) => {
    appendParam(params, 'status', query.status);
    appendParam(params, 'visibility', query.visibility);
    appendParam(params, 'deleted', query.deleted);
    appendTrimmedParam(params, 'tag', query.tag);
    appendTrimmedParam(params, 'q', query.q);
    appendParam(params, 'dateFrom', query.dateFrom);
    appendParam(params, 'dateTo', query.dateTo);
    appendParam(params, 'page', query.page);
    appendParam(params, 'size', query.size);
    appendTrimmedParam(params, 'sort', query.sort);
  });

  const { data } = await api.get<BlogPageResponse<PostSummaryDto>>(`/api/admin/v1/posts${queryString}`);
  return data;
};

export const publishPost = async (id: number, payload?: PublishPostRequest | null): Promise<PostDto> => {
  const { data } = await api.post<PostDto>(`/api/admin/v1/posts/${id}/publish`, payload ?? undefined);
  return data;
};

export const schedulePost = async (id: number, payload: SchedulePostRequest): Promise<PostDto> => {
  const { data } = await api.post<PostDto>(`/api/admin/v1/posts/${id}/schedule`, payload);
  return data;
};

export const unpublishPost = async (id: number): Promise<PostDto> => {
  const { data } = await api.post<PostDto>(`/api/admin/v1/posts/${id}/unpublish`);
  return data;
};

export const archivePost = async (id: number): Promise<PostDto> => {
  const { data } = await api.post<PostDto>(`/api/admin/v1/posts/${id}/archive`);
  return data;
};

export const restorePost = async (id: number): Promise<PostDto> => {
  const { data } = await api.post<PostDto>(`/api/admin/v1/posts/${id}/restore`);
  return data;
};

export const generateSlug = async (payload: SlugGenerationRequest): Promise<SlugGenerationResponse> => {
  const { data } = await api.post<SlugGenerationResponse>('/api/admin/v1/posts/slug:generate', payload);
  return data;
};

export const checkSlugAvailability = async (slug: string): Promise<SlugAvailabilityResponse> => {
  const queryString = buildQueryString((params) => {
    appendTrimmedParam(params, 'slug', slug);
  });
  const { data } = await api.get<SlugAvailabilityResponse>(`/api/admin/v1/posts/slug:check${queryString}`);
  return data;
};

export const previewPost = async (payload: PostPreviewRequest): Promise<PostPreviewResponse> => {
  const { data } = await api.post<PostPreviewResponse>('/api/admin/v1/posts/preview', payload);
  return data;
};

export const bulkUpdatePostStatus = async (
  payload: BulkStatusUpdateRequest
): Promise<BulkStatusUpdateResponse> => {
  const { data } = await api.post<BulkStatusUpdateResponse>('/api/admin/v1/posts:bulk-status', payload);
  return data;
};

export const bulkUpdatePostTags = async (payload: BulkTagsUpdateRequest): Promise<BulkTagsUpdateResponse> => {
  const { data } = await api.post<BulkTagsUpdateResponse>('/api/admin/v1/posts:bulk-tags', payload);
  return data;
};

export const addTagsToPost = async (
  postId: number,
  payload: PostTagsUpdateRequest
): Promise<TagAssignmentResponse> => {
  const { data } = await api.post<TagAssignmentResponse>(`/api/admin/v1/posts/${postId}/tags`, payload);
  return data;
};

export const removeTagsFromPost = async (
  postId: number,
  payload: PostTagsUpdateRequest
): Promise<TagRemovalResponse> => {
  const { data } = await api.delete<TagRemovalResponse>(`/api/admin/v1/posts/${postId}/tags`, {
    data: payload
  });
  return data;
};

export const recalculatePostReadingTime = async (
  postId: number
): Promise<ReadingTimeRecalculationResponse> => {
  const { data } = await api.post<ReadingTimeRecalculationResponse>(
    `/api/admin/v1/posts/${postId}:recalc-reading-time`
  );
  return data;
};

export const recalculateAllReadingTimes = async (
  all: boolean
): Promise<OperationScheduledResponse> => {
  const queryString = buildQueryString((params) => {
    appendParam(params, 'all', all);
  });
  const { data } = await api.post<OperationScheduledResponse>(
    `/api/admin/v1/posts:recalc-reading-time${queryString}`
  );
  return data;
};

export const reindexPost = async (postId: number): Promise<void> => {
  await api.post(`/api/admin/v1/posts/${postId}:reindex`);
};

export const reindexPosts = async (all: boolean): Promise<OperationScheduledResponse> => {
  const queryString = buildQueryString((params) => {
    appendParam(params, 'all', all);
  });
  const { data } = await api.post<OperationScheduledResponse>(`/api/admin/v1/posts:reindex${queryString}`);
  return data;
};

export const exportPost = async (postId: number): Promise<PostDto> => {
  const { data } = await api.get<PostDto>(`/api/admin/v1/posts/${postId}:export`);
  return data;
};

export const importPost = async (payload: PostImportRequest): Promise<PostDto> => {
  const { data } = await api.post<PostDto>('/api/admin/v1/posts:import', payload);
  return data;
};

export const assignHeroMedia = async (postId: number, payload: PostMediaHeroRequest): Promise<PostDto> => {
  const { data } = await api.post<PostDto>(`/api/admin/v1/posts/${postId}/media/hero`, payload);
  return data;
};

export const clearHeroMedia = async (postId: number): Promise<PostDto> => {
  const { data } = await api.delete<PostDto>(`/api/admin/v1/posts/${postId}/media/hero`);
  return data;
};

export const upsertPostMedia = async (
  postId: number,
  payload: PostMediaItemsRequest
): Promise<PostMediaUpdateResponse> => {
  const { data } = await api.put<PostMediaUpdateResponse>(`/api/admin/v1/posts/${postId}/media`, payload);
  return data;
};

export const reorderPostMedia = async (postId: number, payload: PostMediaReorderRequest): Promise<void> => {
  await api.post(`/api/admin/v1/posts/${postId}/media/reorder`, payload);
};

export const removePostMedia = async (
  postId: number,
  mediaId: number,
  role: PostMediaRole
): Promise<void> => {
  const queryString = buildQueryString((params) => {
    appendParam(params, 'role', role);
  });
  await api.delete(`/api/admin/v1/posts/${postId}/media/${mediaId}${queryString}`);
};

export const registerMedia = async (payload: MediaRegisterRequest): Promise<MediaDto> => {
  const { data } = await api.post<MediaDto>('/api/admin/v1/media', payload);
  return data;
};

export const getMedia = async (mediaId: number): Promise<MediaDto> => {
  const { data } = await api.get<MediaDto>(`/api/admin/v1/media/${mediaId}`);
  return data;
};

export const findMedia = async (query: FindMediaQuery = {}): Promise<MediaDto[]> => {
  const queryString = buildQueryString((params) => {
    appendTrimmedParam(params, 'filename', query.filename);
  });
  const { data } = await api.get<MediaDto[]>(`/api/admin/v1/media${queryString}`);
  return data;
};

export const findUnusedMedia = async (
  query: UnusedMediaQuery = {}
): Promise<BlogPageResponse<MediaDto>> => {
  const queryString = buildQueryString((params) => {
    appendParam(params, 'page', query.page);
    appendParam(params, 'size', query.size);
  });
  const { data } = await api.get<BlogPageResponse<MediaDto>>(`/api/admin/v1/media:unused${queryString}`);
  return data;
};

export const deleteMedia = async (mediaId: number): Promise<void> => {
  await api.delete(`/api/admin/v1/media/${mediaId}`);
};

export const createTag = async (payload: TagCreateRequest): Promise<TagDto> => {
  const { data } = await api.post<TagDto>('/api/admin/v1/tags', payload);
  return data;
};

export const listTags = async (query: ListTagsQuery = {}): Promise<BlogPageResponse<TagDto>> => {
  const queryString = buildQueryString((params) => {
    appendTrimmedParam(params, 'q', query.q);
    appendParam(params, 'page', query.page);
    appendParam(params, 'size', query.size);
  });
  const { data } = await api.get<BlogPageResponse<TagDto>>(`/api/admin/v1/tags${queryString}`);
  return data;
};

export const updateTag = async (id: number, payload: TagUpdateRequest): Promise<TagDto> => {
  const { data } = await api.patch<TagDto>(`/api/admin/v1/tags/${id}`, payload);
  return data;
};

export const deleteTag = async (id: number): Promise<void> => {
  await api.delete(`/api/admin/v1/tags/${id}`);
};

export const searchPosts = async (
  query: SearchPostsQuery
): Promise<BlogPageResponse<PostSearchHitDto>> => {
  const queryString = buildQueryString((params) => {
    appendTrimmedParam(params, 'q', query.q);
    appendParam(params, 'page', query.page);
    appendParam(params, 'size', query.size);
  });
  const { data } = await api.get<BlogPageResponse<PostSearchHitDto>>(
    `/api/admin/v1/search/posts${queryString}`
  );
  return data;
};
